<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
  watch,
} from 'vue';

import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import { useContourEventStore } from '@/src/store/contours/contour-event-store';
import { convertPointArrayToArray } from '@/src/store/contours/contour-processing/convertArrayToPointArray';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';
import { ThumbnailCache } from '@/src/store/dicom-web/thumbnail-cache';

const props = defineProps({
  viewerID: { type: String, required: true },
  phaseNumber: { type: Number, required: true },
  currentPhase: { type: Number, required: true },
  currentSeriesIndex: { type: Number, required: true },
  currentSliceIndex: { type: Number, required: true },
  isSelected: { type: Boolean, default: false },
  sliceObj: { type: Object, required: true },
  thumbnailHeight: { type: Number, default: 100 },
});

const emit = defineEmits(['openContextMenu', 'imageClick']);

const currentStudyStore = useCurrentStudyStore();
const contourContainerStore = useContourContainerStore();
const viewportStateStore = useViewportStateStore();
const thumbnailCache = ThumbnailCache.getInstance();
const thumbnailUrl = ref(null);
const compositeImageUrl = ref(null);
const offscreenCanvas = ref(null);
const isFetching = ref(false);
const compositeUpdateQueue = ref(0);
const debounceTimer = ref(null);

// Simple thumbnail cache to avoid regenerating thumbnails
const { phaseNumber, currentSeriesIndex, currentSliceIndex, viewerID }
  = toRefs(props);

const imageUID = computed(() => {
  return currentStudyStore.getImageUID({
    seriesIdx: currentSeriesIndex.value,
    sliceIdx: currentSliceIndex.value,
    imageIdx: phaseNumber.value,
  });
});

const hasState = computed(() => {
  return props.sliceObj.getImageState(props.phaseNumber);
});

const isExcluded = computed(() => {
  return props.sliceObj.isImageExcluded(imageUID.value);
});

const isEnd = computed(() => {
  return props.sliceObj.getIsImageEnd(phaseNumber.value);
});

const isStart = computed(() => {
  return props.sliceObj.getIsImageStart(phaseNumber.value);
});

const contoursPhase = computed(() => {
  return contourContainerStore.getDrawingContours(imageUID.value) || [];
});

const imageData = computed(() => {
  const finalImageData = { imageArray: null, imageDimensions: null };

  if (!imageUID.value) {
    return finalImageData;
  }

  const rawImageData = currentStudyStore.getVtkImageData(imageUID.value);

  if (!rawImageData) {
    return finalImageData;
  }

  finalImageData.imageArray
    = rawImageData.getPointData()?.getScalars()?.getData() || null;
  finalImageData.imageDimensions = rawImageData.getDimensions() || null;

  return finalImageData;
});

const containerStyle = computed(() => {
  return {
    height: `${props.thumbnailHeight}px`,
    width: `${props.thumbnailHeight}px`,
  };
});

function scaleCoordinates(originalDimensions, coordinates, newDimensions) {
  const [originalWidth, originalHeight] = originalDimensions;
  const [newWidth, newHeight] = newDimensions;

  const scaleFactorX = newWidth / originalWidth;
  const scaleFactorY = newHeight / originalHeight;

  const scaledPolyLine = coordinates.map(([x, y]) => [
    x * scaleFactorX,
    y * scaleFactorY,
  ]);

  return scaledPolyLine;
}

function createThumbnailFromImageData(
  imageArray,
  dimensions,
  thumbnailSize = 128,
) {
  const [width, height] = dimensions;

  // Create a canvas to work with the image data
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Set canvas size to original image dimensions
  canvas.width = width;
  canvas.height = height;

  // Create ImageData from the VTK image array
  const canvasImageData = ctx.createImageData(width, height);
  const { data } = canvasImageData;

  // Convert the image array to RGBA format
  // Assuming the input is grayscale, we need to normalize and convert to RGBA
  let min = imageArray[0];
  let max = imageArray[0];

  // Find min and max values for normalization
  for (let i = 0; i < imageArray.length; i++) {
    if (imageArray[i] < min) min = imageArray[i];
    if (imageArray[i] > max) max = imageArray[i];
  }

  const range = max - min;

  // Convert to RGBA
  for (let i = 0; i < imageArray.length; i++) {
    const normalizedValue
      = range > 0 ? ((imageArray[i] - min) / range) * 255 : 0;
    const pixelIndex = i * 4;

    data[pixelIndex] = normalizedValue; // R
    data[pixelIndex + 1] = normalizedValue; // G
    data[pixelIndex + 2] = normalizedValue; // B
    data[pixelIndex + 3] = 255; // A
  }

  // Put the image data on the canvas
  ctx.putImageData(canvasImageData, 0, 0);

  // Calculate aspect ratio and thumbnail dimensions
  const aspectRatio = width / height;
  let thumbnailWidth;
  let thumbnailHeight;

  if (aspectRatio > 1) {
    // Wider than tall
    thumbnailWidth = thumbnailSize;
    thumbnailHeight = thumbnailSize / aspectRatio;
  }
  else {
    // Taller than wide or square
    thumbnailWidth = thumbnailSize * aspectRatio;
    thumbnailHeight = thumbnailSize;
  }

  // Create a thumbnail canvas with proper dimensions
  const thumbnailCanvas = document.createElement('canvas');
  const thumbnailCtx = thumbnailCanvas.getContext('2d');
  thumbnailCanvas.width = thumbnailWidth;
  thumbnailCanvas.height = thumbnailHeight;

  // Draw the original image scaled to thumbnail size while preserving aspect ratio
  thumbnailCtx.drawImage(canvas, 0, 0, thumbnailWidth, thumbnailHeight);

  // Convert to data URL
  return thumbnailCanvas.toDataURL('image/png');
}

async function fetchThumbnail() {
  if (!imageUID.value) return;

  // Check cache first
  const cacheKey = `${imageUID.value}`;
  const cached = thumbnailCache.get(cacheKey);
  if (cached) {
    thumbnailUrl.value = cached;
    return;
  }

  isFetching.value = true;

  try {
    // Get image data directly from the study store
    const vtkImageData = await currentStudyStore.fetchOrGetVtkImageData(
      imageUID.value,
    );

    if (!vtkImageData) {
      console.error('Unable to retrieve image at', imageUID.value);
      thumbnailUrl.value = null;
      return;
    }

    const targetImage = vtkImageData.getPointData().getScalars().getData();
    const dimensions = vtkImageData.getDimensions();

    if (!targetImage || !dimensions) {
      console.error('Invalid image data or dimensions');
      thumbnailUrl.value = null;
      return;
    }

    // Create thumbnail from image data
    const newThumbnailUrl = createThumbnailFromImageData(
      targetImage,
      dimensions,
      props.thumbnailHeight,
    );

    // Cache the thumbnail using singleton cache
    thumbnailCache.set(cacheKey, newThumbnailUrl);

    // Clear the old URL reference
    if (thumbnailUrl.value && thumbnailUrl.value.startsWith('data:')) {
      thumbnailUrl.value = null;
    }

    thumbnailUrl.value = newThumbnailUrl;
  }
  catch (error) {
    console.error('Error creating thumbnail from image data:', error);
    thumbnailUrl.value = null;
  }
  finally {
    isFetching.value = false;
  }
}

function drawContourOnCanvas(ctx, coordinates, color) {
  if (coordinates.length === 0) return;
  const tempCtx = ctx;
  tempCtx.strokeStyle = color;
  tempCtx.lineWidth = 1.1;
  tempCtx.beginPath();

  const [x0, y0] = coordinates[0];
  tempCtx.moveTo(x0, y0);

  for (let i = 1; i < coordinates.length; i++) {
    const [x, y] = coordinates[i];
    tempCtx.lineTo(x, y);
  }

  tempCtx.closePath();
  tempCtx.stroke();
}

function drawContoursOnCanvas(ctx, canvasWidth, canvasHeight) {
  // Get dimensions from imageData
  const dims = imageData.value.imageDimensions;
  if (!dims || dims.length < 2) return;

  const originalDimensions = [dims[0], dims[1]];
  const newDimensions = [canvasWidth, canvasHeight];

  // Draw all contours

  for (const [, ids] of Object.entries(contoursPhase.value)) {
    for (const [, contour] of Object.entries(ids)) {
      if (contour.getProps().userEditable === false) {
        continue;
      }

      const coordinates = convertPointArrayToArray(contour.getPolygonScaled());
      const scaledArray = scaleCoordinates(
        originalDimensions,
        coordinates,
        newDimensions,
      );

      drawContourOnCanvas(ctx, scaledArray, contour.getProps().color);
    }
  }
}

async function createCompositeImage() {
  if (!thumbnailUrl.value) return;

  const currentQueueId = ++compositeUpdateQueue.value;

  try {
    // Create an image element to load the thumbnail
    const img = new Image();

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = (error) => {
        console.error('Error loading image:', thumbnailUrl.value, error);
        reject(error);
      };

      // Data URLs load immediately without network requests
      img.src = thumbnailUrl.value;
    });

    // Check if this is still the latest request
    if (currentQueueId !== compositeUpdateQueue.value) {
      return; // Abandon this request as a newer one is pending
    }

    // Create offscreen canvas for compositing
    if (!offscreenCanvas.value) {
      offscreenCanvas.value = document.createElement('canvas');
    }

    const canvas = offscreenCanvas.value;
    const ctx = canvas.getContext('2d');

    // Use the image's natural dimensions instead of forcing square
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw the thumbnail image at its natural size
    ctx.drawImage(img, 0, 0);

    // Draw contours on top using the image's actual dimensions
    drawContoursOnCanvas(ctx, canvas.width, canvas.height);

    // Final check before updating the display
    if (currentQueueId === compositeUpdateQueue.value) {
      compositeImageUrl.value = canvas.toDataURL('image/png');
    }
  }
  catch (error) {
    console.error('Error creating composite image:', error);
    if (currentQueueId === compositeUpdateQueue.value) {
      compositeImageUrl.value = null;
    }
  }
}

function debouncedCreateCompositeImage() {
  // Clear existing timer
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }

  // Set new timer with a slightly longer delay for the first image
  const delay = compositeImageUrl.value ? 15 : 50; // Longer delay if no existing image
  debounceTimer.value = setTimeout(() => {
    createCompositeImage();
    debounceTimer.value = null;
  }, delay);
}

// contour events
const unsub = useContourEventStore().on((event) => {
  if (event.UIDs.includes(imageUID.value)) {
    debouncedCreateCompositeImage();
  }
});
onBeforeUnmount(unsub);

function handleImageClick(event) {
  const { ctrlKey, shiftKey } = event;

  if (ctrlKey || shiftKey) {
    // Multi-selection mode
    emit('imageClick', {
      imageIndex: phaseNumber.value,
      ctrlKey,
      shiftKey,
    });
  }
  else {
    // Regular selection mode
    viewportStateStore.setSelectedImageIndices(viewerID.value, {
      seriesIdx: currentSeriesIndex.value,
      sliceIdx: currentSliceIndex.value,
      imageIdx: phaseNumber.value,
    });
    emit('imageClick', {
      imageIndex: phaseNumber.value,
      ctrlKey: false,
      shiftKey: false,
    });
  }
}

function openContextMenu(event) {
  event.preventDefault();
  const indexes = {
    seriesIdx: currentSeriesIndex.value,
    sliceIdx: currentSliceIndex.value,
    imageIdx: phaseNumber.value,
  };
  emit('openContextMenu', {
    x: event.clientX,
    y: event.clientY,
    indexes,
    imageUID: imageUID.value,
  });
}

onMounted(() => {
  fetchThumbnail();
});

watch(imageUID, () => {
  fetchThumbnail();
});

// Watch for changes and recreate composite with debouncing
watch(
  [thumbnailUrl],
  () => {
    debouncedCreateCompositeImage();
  },
  { deep: true },
);

onUnmounted(() => {
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }
});
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
  <div
    class="phase-image-container"
    :class="{
      'default-slider-phase': !!hasState,
    }"
    :style="containerStyle"
    @click="handleImageClick"
    @contextmenu.prevent="openContextMenu"
  >
    <div
      v-if="thumbnailHeight > 50"
      class="phase-number-container simple-panel px-2 py-1"
    >
      <p>
        {{ currentSeriesIndex + 1 }}/{{ currentSliceIndex + 1 }}/{{
          phaseNumber + 1
        }}
      </p>
    </div>

    <!-- Left & Right Handler -->
    <div v-if="isStart" class="left-handler" />
    <div v-if="isEnd" class="right-handler" />

    <!-- Excluded image overlay -->
    <div v-if="isExcluded" class="excluded-image-overlay">
      <v-icon icon="mdi-cancel" />
      <h6 class="ml-2">
        Excluded
      </h6>
    </div>

    <div class="thumbnail-container">
      <img
        v-if="compositeImageUrl && !isFetching"
        :src="compositeImageUrl"
        class="composite-thumbnail"
        alt=""
        :class="{
          'selected-image': isSelected,
          'active-phase': currentPhase === phaseNumber,
        }"
      >
      <div v-else class="thumbnail-loading">
        <v-progress-circular
          indeterminate
          size="30"
          color="var(--md-primary)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.phase-number-container {
  pointer-events: none;
  position: absolute;
  z-index: 10;
  bottom: 5px;
  right: 5px;
  border-radius: 8px;
}

.phase-image-container {
  overflow: hidden;
  position: relative;
}

.default-slider-phase {
  border-top: 2px solid var(--md-primary);
  border-bottom: 2px solid var(--md-primary);
}

.active-phase {
  border: 2px solid var(--md-tertiary);
  border-radius: 5px;
}

.selected-image {
  border: 2px solid var(--md-secondary);
  border-radius: 5px;
  z-index: 9;
}

.excluded-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--md-bg-transparent);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9;
}

.left-handler {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  background: var(--md-primary);
  min-width: 8px;
  min-height: 100%;
  border-top-right-radius: 80px 80px;
  border-bottom-right-radius: 80px 80px;
}

.right-handler {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  background: var(--md-primary);
  min-width: 8px;
  min-height: 100%;
  border-top-left-radius: 80px 80px;
  border-bottom-left-radius: 80px 80px;
}

.thumbnail-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.composite-thumbnail {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
  pointer-events: none;
}

.thumbnail-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: var(--md-bg);
}
</style>
